import type { User } from '@klear/user'
import type { UserListSort } from '@klear/user/user-list.wire'
import type { RouterInputs, RouterOutputs } from '@klear/ui/lib/trpc'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import isNumber from 'lodash/isNumber'
import times from 'lodash/times'

type UserListInput = RouterInputs['user']['list']
export type UserListPage = RouterOutputs['user']['list']

export type UserListOptions = {
  traderId?: number
  page?: number
  pageSize?: number
  limit?: number
  sort?: UserListSort
  fetchAll?: boolean
}

type UserListOptionsArg = UserListOptions | number

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 250

function resolveUserListLimit(options?: UserListOptions): number {
  return options?.limit ?? options?.pageSize ?? DEFAULT_LIMIT
}

function normalizeUserListOptions(options?: UserListOptionsArg): UserListOptions & { page: number; limit: number } {
  if (isNumber(options)) {
    return { traderId: options, page: DEFAULT_PAGE, limit: DEFAULT_LIMIT, fetchAll: true }
  }

  return {
    traderId: options?.traderId,
    page: options?.page ?? DEFAULT_PAGE,
    limit: resolveUserListLimit(options),
    fetchAll: options?.fetchAll ?? true
  }
}

function buildUserListInput(options: UserListOptions & { page: number; limit: number }, page: number): UserListInput {
  return {
    page,
    limit: options.limit,
    traderId: options.traderId ?? undefined,
    sort: options.sort?.length ? options.sort : undefined
  }
}

type UserListItem = UserListPage['items'][number]

type UserListPagesResult = {
  users: UserListItem[]
  pagination: UserListPage | undefined
  error: ReturnType<typeof trpc.user.list.useQuery>['error']
  isLoading: boolean
}

type UserListQuerySlice = {
  data?: UserListPage
  isLoading: boolean
  error: UserListPagesResult['error']
}

function combineUserListPages(
  fetchAll: boolean,
  firstPageQuery: UserListQuerySlice,
  additionalPageQueries: UserListQuerySlice[]
): UserListPagesResult {
  const totalPages = firstPageQuery.data?.totalPages ?? 0
  const isLoadingAdditional = fetchAll && totalPages > 1 && additionalPageQueries.some((query) => query.isLoading)
  const isLoading = firstPageQuery.isLoading || isLoadingAdditional

  const users = fetchAll
    ? [...(firstPageQuery.data?.items ?? []), ...additionalPageQueries.flatMap((query) => query.data?.items ?? [])]
    : (firstPageQuery.data?.items ?? [])

  const pagination = fetchAll ? undefined : firstPageQuery.data
  const error = firstPageQuery.error ?? additionalPageQueries.find((query) => query.error)?.error ?? null

  return { users, pagination, error, isLoading }
}

function useUserListPages(options?: UserListOptionsArg): UserListPagesResult {
  const normalized = normalizeUserListOptions(options)
  const { fetchAll, page } = normalized
  const firstPageInput = buildUserListInput(normalized, fetchAll ? DEFAULT_PAGE : page)

  const firstPageQuery = trpc.user.list.useQuery(firstPageInput)
  const totalPages = firstPageQuery.data?.totalPages ?? 0
  const additionalPageQueries = trpc.useQueries((t) => {
    if (!fetchAll || totalPages <= 1) {
      return []
    }

    return times(totalPages - 1, (index) => t.user.list(buildUserListInput(normalized, index + 2)))
  })

  return combineUserListPages(fetchAll ?? true, firstPageQuery, additionalPageQueries)
}

function useUserListWithActivityPages(options?: UserListOptionsArg): UserListPagesResult {
  const normalized = normalizeUserListOptions(options)
  const { fetchAll, page } = normalized
  const firstPageInput = buildUserListInput(normalized, fetchAll ? DEFAULT_PAGE : page)

  const firstPageQuery = trpc.user.listWithActivity.useQuery(firstPageInput)
  const totalPages = firstPageQuery.data?.totalPages ?? 0
  const additionalPageQueries = trpc.useQueries((t) => {
    if (!fetchAll || totalPages <= 1) {
      return []
    }

    return times(totalPages - 1, (index) => t.user.listWithActivity(buildUserListInput(normalized, index + 2)))
  })

  return combineUserListPages(fetchAll ?? true, firstPageQuery, additionalPageQueries)
}

export function useUsers(options?: UserListOptionsArg) {
  return useUserListPages(options)
}

export function useUser(id: number) {
  const { data: user, error, isLoading } = trpc.user.get.useQuery({ id }, { enabled: !!id })

  return { user, error, isLoading }
}

export function useUsersWithActivity(options?: UserListOptionsArg) {
  return useUserListWithActivityPages(options)
}

const invalidateQueries = ['user.list', 'user.get', 'user.listWithActivity']

export function useUserMutations() {
  const createUserMutation = useMutationWithToast(
    trpc.user.create.useMutation,
    createToastConfig<User | null>('User', 'create', (data) => data?.email ?? ''),
    {
      invalidateQueries
    }
  )

  const createOrInviteUserMutation = useMutationWithToast(
    trpc.user.createOrInvite.useMutation,
    createToastConfig<User | null>('User', 'create', (data) => data?.email ?? ''),
    {
      invalidateQueries
    }
  )

  const updateUserMutation = useMutationWithToast(
    trpc.user.update.useMutation,
    createToastConfig<User | null>('User', 'update', (data) => data?.email ?? ''),
    {
      invalidateQueries
    }
  )

  const deleteUserMutation = useMutationWithToast(trpc.user.delete.useMutation, createToastConfig('User', 'delete'), {
    invalidateQueries
  })

  const restoreUserMutation = useMutationWithToast(
    trpc.user.restore.useMutation,
    createToastConfig<User | null>('User', 'restore', (data) => data?.email ?? ''),
    {
      invalidateQueries
    }
  )

  const removeFromOrgMutation = useMutationWithToast(
    trpc.user.removeFromOrg.useMutation,
    createToastConfig('User', 'delete'),
    {
      invalidateQueries
    }
  )

  return {
    createUser: createUserMutation.mutateAsync,
    createOrInviteUser: createOrInviteUserMutation.mutateAsync,
    updateUser: updateUserMutation.mutateAsync,
    deleteUser: deleteUserMutation.mutateAsync,
    restoreUser: restoreUserMutation.mutateAsync,
    removeFromOrg: removeFromOrgMutation.mutateAsync,
    isCreating: createUserMutation.isPending,
    isCreatingOrInviting: createOrInviteUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isRestoring: restoreUserMutation.isPending,
    isRemovingFromOrg: removeFromOrgMutation.isPending
  }
}
